/**
 * Ejecuciones de modelo.
 *
 * ⚠️ Nombre LEGACY: la entidad se llama `EjecucionModelo` y las rutas son
 * `/api/ejecuciones`.
 *
 * **La ejecución es la única que conoce el área evaluada** (`ambito` +
 * `id_programa`). Ni el modelo ni la versión la tienen, así que toda vista que
 * necesite el área debe partir de aquí.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

import { defineStore } from 'pinia'
import type { Proceso, LiderFactor } from '@/interfaces/ProcesoAutoevaluacion'
import {getProcesosService, getProcesoByIdService, crearProcesoService, updateProcesoService, cambiarEstadoProcesoService, eliminarProcesoService, getInstrumentosPendientesService, getLideresProcesoService, asignarLiderProcesoService, eliminarLiderProcesoService} from '@/services/ProcesoAutoevaluacionService'

/** Store `procesos`. Ver la cabecera del archivo para el contrato del módulo. */
export const useGestionProcesos = defineStore('procesos', {
  state: () => ({
    procesos: [] as Proceso[],
    procesoActual: null as Proceso | null
  }),

  actions: {
    async cargarTodos(params?: {
      id_programa?: number
      id_version?: number
      estado?: string
      periodo?: string
    }): Promise<void> {
      this.procesos = await getProcesosService(params)
    },

    async cargarPorId(id: number): Promise<Proceso | null> {
      const proceso = await getProcesoByIdService(id)
      this.procesoActual = proceso
      return proceso
    },

    async crear(
      data: Omit<Proceso, 'id' | 'estado' | 'created_at' | 'updated_at' | 'area' | 'version' | 'lideresFactor'>
    ): Promise<{ ok: boolean; data?: Proceso; errors?: Record<string, string[]> }> {
      const result = await crearProcesoService(data)
      if (result.ok && result.data) {
        this.procesos.push(result.data)
        this.procesoActual = result.data
      }
      return result
    },

    async actualizar(
      id: number,
      payload: Partial<Omit<Proceso, 'id' | 'estado' | 'created_at' | 'updated_at' | 'area' | 'version' | 'lideresFactor'>>
    ): Promise<{ ok: boolean; data?: Proceso; errors?: Record<string, string[]> }> {
      const result = await updateProcesoService(id, payload)
      if (result.ok && result.data) {
        const idx = this.procesos.findIndex(p => p.id === id)
        if (idx !== -1) this.procesos.splice(idx, 1, result.data)
        this.procesoActual = result.data
      }
      return result
    },

    async cambiarEstado(
      id: number,
      estado: Proceso['estado']
    ): Promise<{ ok: boolean; data?: Proceso; errors?: Record<string, string[]> }> {
      const result = await cambiarEstadoProcesoService(id, estado)
      if (result.ok && result.data) {
        const idx = this.procesos.findIndex(p => p.id === id)
        if (idx !== -1) this.procesos.splice(idx, 1, result.data)
        this.procesoActual = result.data
      }
      return result
    },

    async eliminar(id: number): Promise<{ ok: boolean; errors?: Record<string, string[]> }> {
      const result = await eliminarProcesoService(id)
      if (result.ok) {
        this.procesos = this.procesos.filter(p => p.id !== id)
        if (this.procesoActual?.id === id) this.procesoActual = null
      }
      return result
    },

    // Instrumentos vinculados a la versión del proceso que no han sido aplicados
    async getInstrumentosPendientes(id_proceso: number): Promise<any[]> {
      return await getInstrumentosPendientesService(id_proceso)
    },

    // --- Líderes de factor ---
    async getLideres(id_proceso: number): Promise<LiderFactor[]> {
      return await getLideresProcesoService(id_proceso)
    },

    async asignarLider(
      id_proceso: number,
      payload: { id_componente: number; id_usuario: number }
    ): Promise<{ ok: boolean; data?: LiderFactor; errors?: Record<string, string[]> }> {
      return await asignarLiderProcesoService(id_proceso, payload)
    },

    async eliminarLider(
      id_proceso: number,
      id_lider: number
    ): Promise<{ ok: boolean; message?: string }> {
      return await eliminarLiderProcesoService(id_proceso, id_lider)
    }
  }
})
